import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useParticipant } from "../../contexts/ParticipantContext";
import { Heading } from "../../design/heading";
import LoadingView from "../../components/LoadingView";
import { Avatar } from "../../design/avatar";
import { useSeenState } from "../../contexts/SeenStateContext";
import ParticipantEventsList from "./components/ParticipantEventsList";
import ErrorView from "../../components/ErrorView";
import Breadcrumbs from "../../components/Breadcrumbs";
import DeleteParticipantButton from "./components/DeleteParticipantButton";
import ParticipantEducation from "./components/ParticipantEducation";
import ParticipantEmployment from "./components/ParticipantEmployment";
import ParticipantIncome from "./components/ParticipantIncome";
import { Navbar, NavbarItem } from "../../design/navbar";
import { Link } from "../../design/link";
import ParticipantDetails from "./components/ParticipantDetails";
import ParticipantButton from "../../components/ParticipantButton";
import ParticipantFilesList from "./components/ParticipantFilesList";
import { FaLinkedin } from "react-icons/fa";

const LinkedInIcon = FaLinkedin;

const ParticipantDetailPage: React.FC = () => {
  const { recordSeenState } = useSeenState();
  const { participantId } = useParams<{ participantId: string }>();
  const { selectedParticipant, setSelectedParticipant, error, loading } =
    useParticipant();
  const [selectedTab, setSelectedTab] = useState<
    | "details"
    | "education"
    | "employment"
    | "income"
    | "monthlyExpenses"
    | "files"
  >("details");
  const [isEnrichDialogOpen, setIsEnrichDialogOpen] = useState(false);

  useEffect(() => {
    if (participantId) {
      setSelectedParticipant(participantId);
      recordSeenState({ participantId });
    }
  }, [participantId]);

  if (loading.details) {
    return <LoadingView title="Loading Participant" />;
  }

  if (error.details) {
    return <ErrorView error={error.details} />;
  }

  if (!selectedParticipant) {
    return <ErrorView error={new Error("Participant not found")} />;
  }

  return (
    <div>
      <Breadcrumbs
        items={[
          { label: "Participants", href: "/participants" },
          {
            label: selectedParticipant.name,
            href: `/participant/${selectedParticipant._id}`,
          },
        ]}
      />

      <div className="flex flex-col w-full gap-6 md:flex-row">
        <div className="w-full gap-6">
          <div>
            <div className="flex items-end justify-between gap-4">
              <div className="flex gap-4">
                {selectedParticipant.avatarUrl && (
                  <Avatar
                    src={selectedParticipant.avatarUrl}
                    alt={selectedParticipant.name}
                    className="w-12 h-12 rounded-full"
                  />
                )}
                <div className="items-center">
                  <Heading>{selectedParticipant.name}</Heading>
                  {selectedParticipant.linkedInUrl && (
                    <Link href={selectedParticipant.linkedInUrl}>
                      <LinkedInIcon />
                    </Link>
                  )}
                </div>
              </div>
              <div className="flex gap-4">
                <ParticipantButton participant={selectedParticipant} />
                <DeleteParticipantButton participantId={participantId!} />
              </div>
            </div>

            <div className="isolate mt-2.5 flex flex-wrap justify-between gap-x-6 gap-y-4">
              <div className="flex gap-4"></div>
            </div>
          </div>

          <Navbar>
            <NavbarItem
              current={selectedTab === "details"}
              onClick={() => setSelectedTab("details")}
            >
              Details
            </NavbarItem>
            <NavbarItem
              current={selectedTab === "education"}
              onClick={() => setSelectedTab("education")}
            >
              Education
            </NavbarItem>
            <NavbarItem
              current={selectedTab === "employment"}
              onClick={() => setSelectedTab("employment")}
            >
              Employment
            </NavbarItem>
            <NavbarItem
              current={selectedTab === "files"}
              onClick={() => setSelectedTab("files")}
            >
              Files
            </NavbarItem>
          </Navbar>

          {selectedTab === "details" && (
            <ParticipantDetails participant={selectedParticipant} />
          )}
          {selectedTab === "education" && (
            <ParticipantEducation participant={selectedParticipant} />
          )}
          {selectedTab === "employment" && (
            <ParticipantEmployment participant={selectedParticipant} />
          )}
          {selectedTab === "files" && (
            <ParticipantFilesList participantId={participantId!} />
          )}

          {/* PARTICIPANT EVENTS */}
          <ParticipantEventsList participantId={participantId!} />
        </div>
      </div>
    </div>
  );
};

export default ParticipantDetailPage;
